export const FEATURE_FLAGS = {
  SHOW_EXPLORE_SIDEBAR: false,
  SHOW_ADMIN_FEED_SIDEBAR: false,
  /**
   * The anonymous public-profile view beacon (issue #663). ENABLED.
   *
   * WHY IT SHIPPED OFF FIRST. A merge to `dev` or `main` starts two independent
   * deployments of the same commit — Vercel builds the frontend, and the
   * Supabase GitHub integration deploys the Edge Functions declared in
   * supabase/config.toml — and neither waits for the other. So a single pull
   * request cannot guarantee that `record-public-profile-view` exists before
   * the browser starts calling it. PR #664 landed the function with this off,
   * making that window a decision rather than a race.
   *
   * WHY IT IS ON NOW. That function is deployed and verified live on
   * `compaire-dev` (ACTIVE, verify_jwt = false), so the receiving end exists
   * before the client call does. Turning this on is the whole behavioural
   * change: the public organization page posts one slug per successful load
   * and the endpoint owns every meaningful field.
   *
   * ROLLBACK is the same switch: set it back to `false` and redeploy the
   * frontend. That disables the client call without touching the Edge
   * Function, any grant, or any base-table RLS policy. The historical-gap
   * notice in src/lib/analyticsCompleteness.ts is NOT part of the rollback — it
   * reverts to the ongoing-gap wording, which is again the true statement once
   * collection stops.
   *
   * PER-ENVIRONMENT, WITH NO EDIT REQUIRED. This is a compiled constant, so
   * each environment gets it from its own build: production keeps the
   * pre-enablement behaviour, and the ongoing-gap notice, until the
   * `dev` → `main` promotion deploys this commit there. Nothing else has to be
   * changed at promotion time — the analytics notice deliberately names no
   * enablement date, so the same source is true in both environments.
   */
  RECORD_PUBLIC_PROFILE_VIEWS: true,
  /**
   * Basic listings (#1039) in Explore and Compare (#1041, #1042), read through
   * `list_listed_organization_cards()` (migration 20261022130000).
   *
   * SHIPS OFF for the same reason RECORD_PUBLIC_PROFILE_VIEWS did: the merge
   * that adds the reader also adds the client call, and the frontend can
   * deploy (and the authenticated smoke runs against compaire-dev) before the
   * migration exists there. Off, the client never calls the reader and Explore
   * and Compare show published organizations only, exactly as before.
   *
   * ON since the migration was confirmed on compaire-dev (2026-10-22: ledger
   * has 20261022130000, the reader exists, authenticated-only EXECUTE). In
   * production the frontend and the migration arrive in the same dev → main
   * promotion; the promotion checklist's migration gate must show
   * 20261022130000 applied before this build serves traffic, or Explore shows
   * its "listings couldn't load" notice (published results still show).
   * Rollback is the same switch: set it back to false and redeploy.
   */
  LISTINGS_IN_EXPLORE: true,
  /**
   * "Claim this organization" requests (#498 claim slice): the listed page's
   * claim form, the work-email code and the Console Claims queue read
   * get_org_claim_target / request_org_claim / withdraw_org_claim (migration
   * 20261023130000) and the org-claim-email-code and console-org-claims Edge
   * Functions.
   *
   * SHIPS OFF for the same deploy-order reason as LISTINGS_IN_EXPLORE: the
   * frontend can deploy before the migration and the two functions exist on
   * an environment. Off, the listed page keeps its "email support" claim
   * button and nothing calls the new RPCs or functions.
   *
   * READ FROM THE ENVIRONMENT, like ORG_WORKSPACE_V2 below: it must be ON in
   * compaire-dev and OFF in compaire-prod from one commit, so a promotion
   * cannot switch it on in production. `VITE_ORG_CLAIM_REQUESTS=true` is set
   * on the compaire-dev Vercel project (all its environments, including
   * previews); it is absent from compaire-prod, and absent means off.
   * Production turns on by setting the variable and redeploying, after the
   * promotion that carries migration 20261023130000 and both functions.
   * Rollback is unsetting it and redeploying.
   */
  ORG_CLAIM_REQUESTS: import.meta.env.VITE_ORG_CLAIM_REQUESTS === "true",
  /**
   * The org-admin workspace redesign (spec 2026-09-16): the ink shell theme and,
   * from PR 3, the chapter-based story editor. One flag, both effects.
   *
   * READ FROM THE ENVIRONMENT ON PURPOSE. The other flags here are compiled
   * constants, which makes them per-commit — the same source is on or off in
   * both environments. This one must be ON in compaire-dev and OFF in
   * compaire-prod from one commit, so it reads a build-time variable:
   * `VITE_ORG_WORKSPACE_V2=true` is set on the compaire-dev Vercel project and
   * in a developer's .env.local; it is absent from compaire-prod, and absent
   * means off. Production turns on by setting the variable and redeploying —
   * no commit, no promotion. Rollback is unsetting it.
   */
  ORG_WORKSPACE_V2: import.meta.env.VITE_ORG_WORKSPACE_V2 === "true",
  /**
   * The agent-side "bright" redesign (spec 2026-09-24): the light Gmail-style
   * rail, the cool grey canvas and, in later PRs, the org story page.
   *
   * READ FROM THE ENVIRONMENT, like ORG_WORKSPACE_V2 above and for the same
   * reason: ON in compaire-dev and OFF in compaire-prod from one commit.
   * `VITE_AGENT_UI_V2=true` goes on the compaire-dev Vercel project and in a
   * developer's `.env.development.local` (NOT `.env.local`: vitest reads
   * `.env.local`, which would turn the flag on under test). Absent means off.
   * Production turns on by setting the variable and redeploying; rollback is
   * unsetting it. The value is compiled in, so a prerendered page reflects the
   * build it came from.
   */
  AGENT_UI_V2: import.meta.env.VITE_AGENT_UI_V2 === "true",
  /**
   * The agent "My brokerage" view (spec 2026-10-01): the rail item, the
   * /my-brokerage routes and the Feed card. Only meaningful in the bright
   * agent shell.
   *
   * READ FROM THE ENVIRONMENT, and for now set in a developer's
   * `.env.development.local` ONLY (not `.env.local`, which vitest reads).
   * Absent means off, so both deployed environments behave as if this did not
   * exist. Turning it on for compaire-dev or compaire-prod is a founder
   * decision: the page tells an agent how many brokerages fit them better than
   * their current one (spec §10), and the compensation-vocabulary check in
   * spec §12 should be done first.
   */
  MY_BROKERAGE: import.meta.env.VITE_MY_BROKERAGE === "true",

  /**
   * "Open to conversations" (spec 2026-10-01): the agent's switch on Settings,
   * introduction requests on the agent's Messages page, and the "Open agents"
   * tab in Team Management. Read from the environment like the two flags
   * above; absent means off. The functions behind it (migration
   * 20261001150000) exist either way and every agent starts closed, so this
   * only decides whether anyone is offered the switch. Turning it on in
   * production is a founder decision.
   */
  OPEN_TO_CONVERSATIONS: import.meta.env.VITE_OPEN_TO_CONVERSATIONS === "true",
};

/**
 * Org-admin navigation entries.
 *
 * THREE OF THESE READ THE ENVIRONMENT, AND NO DEPLOYED ENVIRONMENT SETS THEM.
 * `showOrgDashboard`, `showOrgRankings` and `showOrgBilling` are views still
 * being built: useful on a developer's machine, not ready for anyone else.
 * They were previously hardcoded `false`, which meant the only way to see them
 * locally was to edit this file and remember never to commit it — an
 * uncommitted edit sitting in the main checkout, invisible to review, blocking
 * `git pull --ff-only`, and one `git add -A` away from shipping.
 *
 * So they read build-time variables instead, and ABSENT MEANS OFF. The
 * variables are set in a developer's `.env.development.local` and NOWHERE
 * ELSE — not on compaire-dev, not on compaire-prod. That is the difference
 * from ORG_WORKSPACE_V2 and AGENT_UI_V2 above, which ARE on in compaire-dev;
 * these are local-only, so both deployed environments behave exactly as they
 * did when the values were hardcoded `false`. Nothing about this change is
 * observable outside a developer's machine.
 *
 * `.env.development.local` specifically, NOT `.env.local`: vitest reads
 * `.env.local`, which would turn these on under test and break the flag-off
 * snapshots. Vite loads `.env.development.local` for `vite dev` only, so a
 * production build cannot pick them up either.
 *
 * `showOrgAnalytics` stays a compiled constant. It is on everywhere on purpose.
 */
export const ORG_NAV_FEATURE_FLAGS = {
  showOrgDashboard: import.meta.env.VITE_SHOW_ORG_DASHBOARD === "true",
  showOrgAnalytics: true,
  showOrgRankings: import.meta.env.VITE_SHOW_ORG_RANKINGS === "true",
  showOrgBilling: import.meta.env.VITE_SHOW_ORG_BILLING === "true",
};
